// The Hiring Request screens' words (lane B2 S08): the Requests list, the Hiring Request form, the Hiring Manager's
// certification and the HR Business Partner's approval. Every entry quotes his glossary (G §5 "Hiring Request",
// "Required request fields", "Current JD", the request chain) or the vendored design-system excerpts; the D0 label
// suites discover this module (a lane folder of src/views) and hold it to the same rules as src/mc. Words the design
// system already defines (the request chain, Certify · Approve · Return, Save, Submit, the journey and parallel tiles,
// Original · Revised, History, Certification required first …) are imported from @/mc, never redefined here.
// A required field he has not named (V1's business unit, brands supported, reports-to title, last day, level and title
// unchanged) is not drawn from this module: see ./compat.ts.

import { defineLabels, template } from "@/mc/labels";

const HIRING_REQUEST = "| Hiring Request / request | Spec object \"HiringRequest\"; \"HRBP creates request and drafts justification/data; app routes complete request + current JD to HM.\"";
const ROUTES = "\"HRBP creates request and drafts justification/data; app routes complete request + current JD to HM.\"";
const FIELDS =
  "\"title, HM, new/replacement, replacement employee, location, openings, people-manager/direct reports, posting type, notes, department, job family, area of interest, job code, org alignment, headcount approval, cost center, justification, final posting validation.\"";
const RETURNED = "\"Returned requests go back to Draft or Dormant without deleting history.\"";
const CURRENT_JD = "| Current JD / Job Description version | \"app routes complete request + current JD to HM\"; object \"JobDescriptionVersion\"; \"HRBP updates PayFactors if JD changed\".";
const POSTING_SCOPE = "| posting scope (`internal_only`/`external`/`confidential`) |";
const PHASE_FILTER = "| Phase | a filter (\"status, phase, health\");";
const CHAIN = "Chain \"Draft -> Pending HM Certification -> HM Certified -> Pending HRBP Approval -> Approved.\"";
const HRBP_APPROVAL = "| HRBP approval | \"Keep HRBP as final approver.\" \"HRBP approval triggers TA and Compensation in parallel.\"";
const HM_CERTIFICATION = "| HM certification | \"Add HM certification as a distinct state and action.\" \"HM reviews, corrects and certifies.\"";
const OWNERSHIP =
  "\"Field-level edit ownership: HRBP business/org fields; Recruiter strategy/interview fields; Coordinator posting/scheduling/admin fields; shared fields use reason + audit.\"";

/** The screen names and the JD pane. */
export const REQUEST_PAGE_LABELS = /* @__PURE__ */ defineLabels({
  requests: { text: "Requests" },
  hiringRequest: { text: "Hiring Request" },
  currentJd: { text: "Current JD" },
  returnedRequest: { text: "Returned request" },
  status: { text: "Status" },
  revisedByHm: { text: "Revised by the Hiring Manager" },
});

/** His required request fields (G §5), in his order, with their first letter capitalised as a field name. */
export const REQUEST_FIELD_LABELS = /* @__PURE__ */ defineLabels({
  title: { text: "Title" },
  newReplacement: { text: "New/replacement" },
  newHeadcount: { text: "New" },
  replacement: { text: "Replacement" },
  replacementEmployee: { text: "Replacement employee" },
  location: { text: "Location" },
  openings: { text: "Openings" },
  peopleManager: { text: "People-manager" },
  directReports: { text: "Direct reports" },
  postingType: { text: "Posting type" },
  notes: { text: "Notes" },
  department: { text: "Department" },
  jobFamily: { text: "Job family" },
  areaOfInterest: { text: "Area of interest" },
  jobCode: { text: "Job code" },
  orgAlignment: { text: "Org alignment" },
  headcountApproval: { text: "Headcount approval" },
  costCenter: { text: "Cost center" },
  justification: { text: "Justification" },
  finalPostingValidation: { text: "Final posting validation" },
});

/** The posting scopes a request names today (V1's three, his G §5 "posting scope" row). */
export const POSTING_SCOPE_LABELS = /* @__PURE__ */ defineLabels({
  internalOnly: { text: "Internal" },
  external: { text: "External" },
  confidential: { text: "Confidential" },
});

/** His rule sentences: a blocked control's reason, a hint, a refusal's banner. */
export const REQUEST_RULES = /* @__PURE__ */ defineLabels({
  routesToHm: { text: "app routes complete request + current JD to HM" },
  hrbpCreates: { text: "HRBP creates request and drafts justification/data; app routes complete request + current JD to HM." },
  requiredFields: { text: "title, HM, new/replacement, replacement employee, location, openings, people-manager/direct reports, posting type, notes, department, job family, area of interest, job code, org alignment, headcount approval, cost center, justification, final posting validation." 


},
  payfactors: { text: "HRBP updates PayFactors if JD changed" },
  returned: { text: "Returned requests go back to Draft or Dormant without deleting history." },
  chain: { text: "Draft -> Pending HM Certification -> HM Certified -> Pending HRBP Approval -> Approved." },
  finalApprover: { text: "Keep HRBP as final approver." },
  hmCorrects: { text: "HM reviews, corrects and certifies." },
  distinctCertification: { text: "Add HM certification as a distinct state and action." },
  fieldOwnership: { text: "Field-level edit ownership: HRBP business/org fields; Recruiter strategy/interview fields; Coordinator posting/scheduling/admin fields; shared fields use reason + audit." 


},
  optimisticConcurrency: { text: "Use optimistic concurrency or equivalent for mutable workflow records." 


},
});

// B13 (DC-234, F-143 / F-148): the request's Timing and budget block, on every surface, and a converted request's line.
const TIMING = "| Timing and budget | The request's block for **Target start date** · **Urgency** · **Target compensation range** · **Position number**";
const URGENCY = "| Urgency · Normal / Urgent | A choice **Normal · Urgent**, nothing preselected on a new request, required before the request routes";
const CONVERTED = "| Converted → R-#### / From Hiring Request RQ-#### | A converted request stays in the lists of everyone who had a seat on it, its status reading **Converted → R-1043** as a link to the requisition";

/** The Timing and budget card (DC-234) and its four fields. */
export const TIMING_LABELS = /* @__PURE__ */ defineLabels({
  timingAndBudget: { text: "Timing and budget" },
  targetStartDate: { text: "Target start date" },
  urgency: { text: "Urgency" },
  targetCompRange: { text: "Target compensation range" },
  positionNumber: { text: "Position number" },
  normal: { text: "Normal" },
  urgent: { text: "Urgent" },
});

/** A converted request's status: a link to the requisition it became (F-148). */
export const convertedTo = /* @__PURE__ */ template({ text: "Converted → {requisition}" });
